import {
  Badge,
  Button,
  Flex,
  FormLabel,
  GridItem,
  Heading,
  IconButton,
  Input,
  InputGroup,
  Link,
  Select,
  SimpleGrid,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import Paginator from '../../../components/paginator'
import { formatDateTime } from '../../../helpers'
import { JobApplicationService } from '../../../services/JobApplication/JobApplicationService'
import {
  JobApplication,
  JobApplicationComplete,
  JobApplicationPositions,
  JobApplicationStatuses
} from '../../../services/JobApplication/types'
import { JobApplicationDeleteDialog } from './JobApplicationDeleteDialog'
import { JobApplicationFormModal } from './JobApplicationFormModal'
import { JobApplicationViewModal } from './JobApplicationViewModal'

export const statusInfo: Record<JobApplicationStatuses, { label: string; color: string }> = {
  new: {
    label: 'Não Visualizado',
    color: 'gray'
  },
  qualified: {
    label: 'Qualificado',
    color: 'green'
  },
  hired: {
    label: 'Contratado',
    color: 'blue'
  },
  incompatible: {
    label: 'Incompatível',
    color: 'red'
  }
}

export default function JobApplicationPage() {
  const service = new JobApplicationService()
  const ref = useRef()
  const [jobApplications, setJobApplications] = useState<JobApplication[]>([])
  const [positions, setPositions] = useState<JobApplicationPositions>([])
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [detailsDeleteDialog, setDetailsDeleteDialog] = useState(false)
  const [targetRecord, setTargetRecord] = useState<JobApplication>()
  const [filters, setFilters] = useState({ search: '', length: '10', status: '', position: '', area: '' })
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [updatingStatus, setUpdatingStatus] = useState<number[]>([])
  const [isEditingStatus, setIsEditingStatus] = useState<number[]>([])
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [recordEditing, setRecordEditing] = useState<JobApplicationComplete>()

  useEffect(() => {
    refreshList()

    service.positions().then((response) => setPositions(response))
  }, [])

  useEffect(() => {
    refreshList()
  }, [filters, page])

  useEffect(() => {
    if (!isCreateModalOpen) {
      refreshList()
    }
  }, [isCreateModalOpen])

  const refreshList = async () => {
    service.list({ ...filters, page }).then((response) => {
      setJobApplications(response.data)
      setTotalPages(Math.ceil(response.recordsTotal / parseInt(filters.length)))
    })
  }

  const handleStatusChange = async (newStatus: JobApplicationStatuses, id: number) => {
    setUpdatingStatus([...updatingStatus, id])

    await service.updateStatus(id, newStatus)

    const applications = [...jobApplications]
    const applicationIndex = applications.findIndex((application) => application.id === id)
    applications[applicationIndex].status = newStatus

    setJobApplications(applications)

    setUpdatingStatus(updatingStatus.filter((statusId) => statusId !== id))
    setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== id))
  }

  const openEditModal = (id: number) => {
    service.get(id).then((response) => {
      setRecordEditing(response)
      setIsCreateModalOpen(true)
    })
  }

  return (
    <>
      <Heading as="h1" size="lg" marginBottom="5" mb="10" textTransform="uppercase">
        Banco de Talentos
      </Heading>
      <SimpleGrid columns={12} spacing={4} mb="5">
        <GridItem colSpan={3}>
          <FormLabel htmlFor="teacher" fontSize="sm" fontWeight="normal">
            Status
          </FormLabel>
          <Select
            id="status"
            size="sm"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value as JobApplicationStatuses })}
          >
            <option value="">Selecione</option>
            {Object.entries(statusInfo).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </Select>
        </GridItem>
        <GridItem colSpan={3}>
          <FormLabel htmlFor="position" fontSize="sm" fontWeight="normal">
            Vaga
          </FormLabel>
          <Select
            id="position"
            size="sm"
            value={filters.position}
            onChange={(e) => setFilters({ ...filters, position: e.target.value })}
          >
            <option value="">Selecione</option>
            {positions.map(({ name }) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </GridItem>
        <GridItem colSpan={3}>
          <FormLabel htmlFor="area" fontSize="sm" fontWeight="normal">
            Área
          </FormLabel>
          <Select
            id="position"
            size="sm"
            value={filters.area}
            onChange={(e) => setFilters({ ...filters, area: e.target.value })}
          >
            <option value="">Selecione</option>
            {positions
              .find((position) => position.name === filters.position)
              ?.areas.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
          </Select>
        </GridItem>
      </SimpleGrid>
      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setFilters({ ...filters, length: e.target.value })}
            value={filters.length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            value={filters.search}
          />
        </Flex>
      </Flex>
      <TableContainer fontSize="smaller" padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th p={1}>ID</Th>
              <Th p={1}>Nome</Th>
              <Th p={1}>Telefone</Th>
              <Th p={1}>E-mail</Th>
              <Th p={1}>Vaga</Th>
              <Th p={1}>Criado em</Th>
              <Th p={1}>Status</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {jobApplications.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center">
                  Nenhuma candidatura cadastrada
                </Td>
              </Tr>
            ) : jobApplications.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center">
                  Nenhuma candidatura encontrada
                </Td>
              </Tr>
            ) : (
              jobApplications.map((application) => (
                <Tr key={application.id}>
                  <Td p={1}>{application.id}</Td>
                  <Td p={1}>{application.name}</Td>
                  <Td p={1}>{application.phone}</Td>
                  <Td p={1}>
                    <Link href={`mailto:${application.email}`} color="blue.400" target="_blank">
                      {application.email}
                    </Link>
                  </Td>
                  <Td p={1}>{application.job_position}</Td>
                  <Td p={1}>{formatDateTime(application.created_at)}</Td>
                  <Td p={1} width="150px" textAlign="left">
                    {isEditingStatus.includes(application.id) ? (
                      <InputGroup display="flex" alignItems="center">
                        <Select
                          size="xs"
                          fontSize={'0.8em'}
                          variant="outline"
                          value={application.status}
                          onChange={(event) =>
                            handleStatusChange(event.target.value as JobApplicationStatuses, application.id)
                          }
                          disabled={updatingStatus.includes(application.id)}
                        >
                          {Object.entries(statusInfo).map(([key, value]) => (
                            <option key={key} value={key}>
                              {value.label}
                            </option>
                          ))}
                        </Select>
                        <Tooltip label="Cancelar" aria-label="Cancelar">
                          <Button
                            size="xs"
                            colorScheme="red"
                            ml="1"
                            onClick={() =>
                              setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== application.id))
                            }
                          >
                            <i className="lni lni-close"></i>
                          </Button>
                        </Tooltip>
                      </InputGroup>
                    ) : (
                      <Tooltip label="Clique duas vezes para editar">
                        <Badge
                          colorScheme={statusInfo[application.status].color}
                          fontSize="0.8em"
                          px="2"
                          onDoubleClick={() => setIsEditingStatus([...isEditingStatus, application.id])}
                        >
                          {statusInfo[application.status].label}
                        </Badge>
                      </Tooltip>
                    )}
                  </Td>
                  <Td p={1}>
                    <Tooltip label="Detalhes" hasArrow placement="top">
                      <IconButton
                        aria-label="Detalhes"
                        colorScheme="magenta"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setTargetRecord(application)
                          setDetailsDeleteDialog(true)
                        }}
                      >
                        <i className="lni lni-zoom-in"></i>
                      </IconButton>
                    </Tooltip>
                    <Tooltip label="Editar" hasArrow placement="top">
                      <IconButton
                        aria-label="Editar"
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          openEditModal(application.id)
                        }}
                      >
                        <i className="lni lni-pencil"></i>
                      </IconButton>
                    </Tooltip>
                    <Tooltip label="Excluir" hasArrow placement="top">
                      <IconButton
                        aria-label="Excluir"
                        colorScheme="red"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setTargetRecord(application)
                          setOpenDeleteDialog(true)
                        }}
                      >
                        <i className="lni lni-trash-can"></i>
                      </IconButton>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      {targetRecord && (
        <>
          <JobApplicationDeleteDialog
            isOpen={openDeleteDialog}
            jobApplication={targetRecord}
            leastDestructiveRef={ref}
            onClose={() => {
              setOpenDeleteDialog(false)
              refreshList()
            }}
          />

          <JobApplicationViewModal
            isOpen={detailsDeleteDialog}
            onClose={() => {
              setDetailsDeleteDialog(false)
            }}
            id={targetRecord.id}
          />
        </>
      )}

      <Tooltip label="Adicionar manualmente" aria-label="Adicionar manualmente" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setRecordEditing(undefined)
            setIsCreateModalOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      <JobApplicationFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        positions={positions}
        jobApplication={recordEditing}
      />
    </>
  )
}
